import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { toast } from '@sales-center/components'
import {
  isAPIError,
  isAPIValidationError,
  validationErrorsToMap,
} from '@user/store/services/crud'
import { getLicenseInfo } from '@user/store/licensing/api'

export type Mode = 'mapped' | 'unmapped' | undefined
export const useGetLicenseInfo = (
  licenseLeft: number | undefined,
  setLicenseLeft: React.Dispatch<React.SetStateAction<number | undefined>>,
) => {
  const dispatch = useAppDispatch()

  const [status, setStatus] = useState<'idle' | 'pending' | 'success' | 'fail'>(
    'idle',
  )

  const [shouldExecute, setShouldExecute] = useState(false)
  const [mode, setMode] = useState<Mode>(undefined)
  const [isUpdate, setIsUpdate] = useState(false)
  const execute = useCallback(
    (mode: Mode, isUpdate?: boolean) => {
      if (mode) setMode(mode)
      if (isUpdate) setIsUpdate(true)
      if (status != 'pending') {
        setShouldExecute(true)
      }
    },
    [status],
  )

  useEffect(() => {
    let mounted = true

    const execute = async () => {
      if (shouldExecute) {
        try {
          setStatus('pending')
          const result = await dispatch(getLicenseInfo())

          if (mounted) {
            if (getLicenseInfo.fulfilled.match(result)) {
              setStatus('success')
              const product = result.payload.find((x) => x.id == 'LM')
              if (product && product.isEnabled) {
                setLicenseLeft(product.availableLicenses)
              } else {
                toast.error(
                  'Product not found or not enabled. Contact your administrator',
                )
              }
            } else {
              if (isAPIError(result.payload)) {
                toast.error(result.payload.Message)
              } else if (isAPIValidationError(result.payload)) {
                const errors = validationErrorsToMap(result.payload)
                if (errors) {
                  toast.error([...errors.values()].flat().join(','))
                }
              } else {
                toast.error(
                  `Unable to get license info. ${result.error.message}`,
                )
              }
              setStatus('fail')
            }
          }
        } catch (error) {
          if (mounted) {
            setStatus('fail')
            toast.error(error.Message)
          }
        } finally {
          setShouldExecute(false)
          setIsUpdate(false)
          setStatus('idle')
        }
      }
    }
    execute()

    return () => {
      mounted = false
    }
  }, [dispatch, isUpdate, setLicenseLeft, shouldExecute])

  return { status, execute, licenseLeft, mode }
}
